import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { Barrel } from "../models/Barrel";
import { deleteBarrel, upsertBarrel } from "../repositories/BarrelsRepository";
import { BARRELS_QUERY_KEYS } from "./useBarrels";

// ============================================
// Types
// ============================================

interface MutationOptions {
	onSuccess?: (data?: Barrel) => void;
	onError?: (error: Error) => void;
}

// ============================================
// useUpsertBarrel Hook
// ============================================

export const useUpsertBarrel = ({
	onSuccess,
	onError,
}: MutationOptions = {}) => {
	const queryClient = useQueryClient();

	const mutation = useMutation({
		mutationFn: async (barrel: Barrel) => {
			return await upsertBarrel(barrel);
		},
		onSuccess: (data) => {
			// Invalidate all barrel queries to force refetch
			queryClient.invalidateQueries({
				queryKey: BARRELS_QUERY_KEYS.all,
			});
			onSuccess?.(data);
		},
		onError: (error: Error) => {
			onError?.(error);
		},
	});

	return {
		upsertBarrel: mutation.mutate,
		upsertBarrelAsync: mutation.mutateAsync,
		isUpserting: mutation.isPending,
	};
};

// ============================================
// useDeleteBarrel Hook
// ============================================

export const useDeleteBarrel = ({
	onSuccess,
	onError,
}: MutationOptions = {}) => {
	const queryClient = useQueryClient();

	const mutation = useMutation({
		mutationFn: async (barrelId: number) => {
			await deleteBarrel(barrelId);
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: BARRELS_QUERY_KEYS.all,
			});
			onSuccess?.();
		},
		onError: (error: Error) => {
			onError?.(error);
		},
	});

	return {
		deleteBarrel: mutation.mutate,
		deleteBarrelAsync: mutation.mutateAsync,
		isDeleting: mutation.isPending,
	};
};
